import type { Brand, BrandDetails } from '@/components/modules/brands-selector';

import { CASINO_SETTINGS } from '../../theme/tokens';
import type { CasinoLogos, LogoEditWindow, LogoVariant } from '../../mock-data/types';
import { FAVICON_SLOT, isOwnerArtwork, type LogoFamily, type LogoGraph } from './logo-api';
import type { PixelSize } from './logo-image';

/**
 * Resolves the logo set shown on the Casino Settings page to ONE render model,
 * preferring the real brand logo assets when the active brand has them.
 *
 * Real source: the per-slot map the branding pipeline produces (`symbol`,
 * `name_horizontal`, `symbol_and_name_vertical`, …), each a `{ src, alt }` CDN asset.
 * `brandLogoSource` resolves it from either spot the API ships it: the brand's own
 * `casino_data.details.logo`, else the deployment record's
 * `status_details.logoAssets.logos`. The top-bar brand switcher reads the same map but
 * only uses one slot; here we surface every slot as a downloadable variant.
 *
 * Fallback: brands whose `logo` is a legacy single URL (or empty) have no variant
 * set, so we draw a schematic lockup per mock variant instead (the same data that
 * gated this page before the API). Both paths yield `DisplayLogo`s the section
 * renders identically.
 */

/** A single asset in the brand's per-slot logo map. */
export type LogoAssetLike = { src?: string; alt?: string };
/** `brand.casino_data.details.logo` — a legacy URL string, or the slot→asset map. */
export type BrandLogoSource = string | Record<string, LogoAssetLike | undefined> | null | undefined;

/**
 * Where a rendered logo came from: the branding pipeline generated it, or the owner
 * replaced it with their own artwork. Surfaced as a label on every logo, so it's always
 * clear which marks are still the generated set.
 */
export type LogoOrigin = 'generated' | 'custom';

export type DisplayLogo = {
  id: string;
  label: string;
  src: string;
  alt: string;
  /**
   * The branding pipeline's own description of what belongs in this slot, straight from
   * the asset's `alt` on the brand record ("The symbol and short name of the casino are
   * displayed horizontally…"). Absent when the record carries none, or for the schematic
   * fallback set — the replace overlay then falls back to its own copy for the slot.
   */
  description?: string;
  /** Suggested download filename. */
  filename: string;
  /** Preview-frame tone the artwork sits on (light artwork → dark frame, etc.). */
  frame: 'light' | 'dark';
  /** True when `src` is a `data:` URI (schematic fallback, or an owner replacement) —
   *  download straight from the URI instead of fetching it. */
  isData: boolean;
  /** Generated by the branding pipeline, or replaced by the owner. */
  origin: LogoOrigin;
};

export type ResolvedLogos = {
  primary: DisplayLogo;
  variants: DisplayLogo[];
  /** 'brand' = real per-brand assets from the API; 'mock' = schematic fallback. */
  source: 'brand' | 'mock';
};

// Human labels for the known pipeline slots; unknown slots are humanised generically.
// The orientation suffixes the pipeline's slot ids carry ("· horizontal", "· vertical")
// are deliberately dropped: an owner picks a logo by what it IS, and the shape is already
// obvious from the preview beside the name.
const SLOT_LABELS: Record<string, string> = {
  symbol_and_name_horizontal: 'Primary logo',
  symbol_and_name_vertical: 'Symbol + name',
  symbol_and_short_name_horizontal: 'Symbol + short name',
  name_horizontal: 'Wordmark',
  name_horizontal_black: 'Wordmark · black',
  name_horizontal_white: 'Wordmark · white',
  short_name: 'Short name',
  symbol: 'Symbol',
  [FAVICON_SLOT]: 'Favicon',
};

// Placement-specific slots the branding pipeline emits but that we don't surface as
// downloadable logo variants on this page (they're app chrome, not brand assets).
const HIDDEN_SLOTS = new Set(['topNavDesktop', 'topNavMobile', 'onboarding', 'footer']);

/**
 * CANONICAL slot sizes — what an owner should supply for each logo.
 *
 * These are the baseline a replacement is measured against. The same asset is rendered in
 * fixed places (site chrome, onboarding pages, transactional emails, the favicon), so a
 * replacement that changes the aspect ratio letterboxes or crops in those placements, and
 * one below this resolution goes soft on high-density screens. The replace flow shows the
 * slot's size + ratio as guidance, fits an upload to it, and warns when a picked file
 * drifts from it (it never blocks: an owner may knowingly supply a different mark).
 *
 * Both numbers are deliberately ROUND, and so is the ratio between them: "1200 × 200,
 * 6:1" is a brief a designer can work to; "1073 × 200, 5.4:1" is a number to squint at.
 * They are therefore a TARGET rather than a readout of the pipeline's own output, which
 * lands on whatever the generator produced:
 *
 *   slot                              pipeline     target        ratio
 *   symbol_and_name_horizontal        1536×1024    1500×1000     3:2   (the primary)
 *   name_horizontal                   1073×200     1200×200      6:1
 *   symbol_and_name_vertical           812×488      800×400      2:1
 *   symbol_and_short_name_horizontal  1066×354     1200×600      2:1
 *   name_horizontal_black / _white    1294×241     1200×200      6:1
 *   short_name                         630×271      800×400      2:1
 *   symbol                             600×449      600×600      1:1
 *   favicon                             64×64       512×512      1:1   (scaled down for the browser tab)
 *
 * Since the target is a tidied shape, existing generated artwork sits a little off it by
 * design — which is exactly why {@link RATIO_TOLERANCE} is loose enough to stay quiet
 * about a near-miss and only speak up about a genuinely different shape.
 */
export const SLOT_SPECS: Record<string, PixelSize> = {
  symbol_and_name_horizontal: { width: 1500, height: 1000 },
  name_horizontal: { width: 1200, height: 200 },
  symbol_and_name_vertical: { width: 800, height: 400 },
  symbol_and_short_name_horizontal: { width: 1200, height: 600 },
  name_horizontal_black: { width: 1200, height: 200 },
  name_horizontal_white: { width: 1200, height: 200 },
  short_name: { width: 800, height: 400 },
  symbol: { width: 600, height: 600 },
  [FAVICON_SLOT]: { width: 512, height: 512 },
};

/** The canonical size for a slot, or undefined for one the pipeline doesn't size. */
export const logoSpec = (slot: string): PixelSize | undefined => SLOT_SPECS[slot];

// Display order for the slots, and which slot stands in as the "primary" showcase.
const SLOT_ORDER = [
  'symbol_and_name_horizontal',
  'name_horizontal',
  'symbol_and_name_vertical',
  'symbol_and_short_name_horizontal',
  'name_horizontal_black',
  'name_horizontal_white',
  'short_name',
  'symbol',
  FAVICON_SLOT,
];

const slug = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'logo';

const humanise = (slot: string): string =>
  SLOT_LABELS[slot] ?? slot.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const extOf = (url: string): string => /\.([a-z0-9]{2,5})(?:$|\?)/i.exec(url)?.[1]?.toLowerCase() ?? 'png';

// White lockups need a dark frame to be visible; everything else previews on a light wash.
const frameFor = (slot: string): 'light' | 'dark' => (/white/i.test(slot) ? 'dark' : 'light');

// --- Schematic fallback artwork ---------------------------------------------
// There is no logo API for these (legacy / unset brands), so each mock variant is
// drawn as a schematic brand lockup (a coloured symbol + the casino wordmark) at its
// native aspect ratio. The same SVG backs the preview and the download. Every colour
// comes from the CASINO_SETTINGS tokens; the artwork is dark-ink (it previews on the
// light frame).
const FONT = 'Satoshi, ui-sans-serif, system-ui, sans-serif';

const initialOf = (name: string): string => (name.trim().charAt(0) || '?').toUpperCase();

const escapeXml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const symbolMarkup = (x: number, y: number, s: number, fill: string, ink: string, initial: string): string =>
  `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${s.toFixed(1)}" height="${s.toFixed(1)}" rx="${(s * 0.24).toFixed(1)}" fill="${fill}"/>` +
  `<text x="${(x + s / 2).toFixed(1)}" y="${(y + s / 2).toFixed(1)}" font-family="${FONT}" font-size="${(s * 0.56).toFixed(1)}" font-weight="700" fill="${ink}" text-anchor="middle" dominant-baseline="central">${escapeXml(initial)}</text>`;

const wordMarkup = (cx: number, cy: number, fontSize: number, fill: string, name: string): string =>
  `<text x="${cx.toFixed(1)}" y="${cy.toFixed(1)}" font-family="${FONT}" font-size="${fontSize.toFixed(1)}" font-weight="800" letter-spacing="-0.02em" fill="${fill}" text-anchor="middle" dominant-baseline="central">${escapeXml(name)}</text>`;

const buildLogoSvg = (variant: LogoVariant, name: string): string => {
  const { width: w, height: h, layout, mono } = variant;
  const ink = CASINO_SETTINGS.logoInkDark;
  const symbolFill = mono ? ink : CASINO_SETTINGS.logoSymbol;
  const symbolInk = mono ? CASINO_SETTINGS.logoInkLight : CASINO_SETTINGS.logoSymbolInk;
  const initial = initialOf(name);

  let inner = '';
  if (layout === 'symbol') {
    const s = Math.min(w, h) * 0.62;
    inner = symbolMarkup((w - s) / 2, (h - s) / 2, s, symbolFill, symbolInk, initial);
  } else if (layout === 'wordmark') {
    inner = wordMarkup(w / 2, h / 2, h * 0.4, ink, name);
  } else if (layout === 'stacked') {
    const s = h * 0.46;
    const sy = h * 0.12;
    inner =
      symbolMarkup((w - s) / 2, sy, s, symbolFill, symbolInk, initial) +
      wordMarkup(w / 2, sy + s + (h - (sy + s)) / 2, h * 0.15, ink, name);
  } else {
    const p = h * 0.18;
    const s = h - p * 2;
    const sx = p * 1.4;
    const gap = h * 0.14;
    inner =
      symbolMarkup(sx, (h - s) / 2, s, symbolFill, symbolInk, initial) +
      wordMarkup((sx + s + gap + (w - p)) / 2, h / 2, h * 0.3, ink, name);
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" fill="none">${inner}</svg>`;
};

const toDataUri = (svg: string): string => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

const mockLogo = (variant: LogoVariant, name: string): DisplayLogo => ({
  id: variant.id,
  label: variant.label,
  src: toDataUri(buildLogoSvg(variant, name)),
  alt: `${name} ${variant.label} logo`,
  filename: `${slug(name)}-${variant.id}.svg`,
  frame: 'light',
  isData: true,
  origin: 'generated',
});

// --- Resolution --------------------------------------------------------------
/** Read the real per-slot map into ordered DisplayLogos (only slots with a `src`). */
const fromBrand = (map: Record<string, LogoAssetLike | undefined>, name: string): DisplayLogo[] => {
  const slots = Object.keys(map).filter((slot) => Boolean(map[slot]?.src) && !HIDDEN_SLOTS.has(slot));
  slots.sort((a, b) => {
    const ai = SLOT_ORDER.indexOf(a);
    const bi = SLOT_ORDER.indexOf(b);
    return (ai === -1 ? SLOT_ORDER.length : ai) - (bi === -1 ? SLOT_ORDER.length : bi);
  });
  return slots.map((slot) => {
    const asset = map[slot];
    const src = asset?.src ?? '';
    // The pipeline's `alt` is a full description of the slot ("The symbol of the casino.
    // No text."), which is exactly the guidance the replace overlay wants — so keep it
    // apart from the synthesised `alt` fallback, which is a name, not a description.
    const description = asset?.alt?.trim() || undefined;
    return {
      id: slot,
      label: humanise(slot),
      src,
      alt: asset?.alt ?? `${name} ${humanise(slot)} logo`,
      description,
      filename: `${slug(name)}-${slug(slot)}.${extOf(src)}`,
      frame: frameFor(slot),
      isData: false,
      origin: 'generated',
    };
  });
};

// --- Owner replacements ------------------------------------------------------
// A replaced logo is not a different KIND of asset: the pipeline stores the owner's artwork
// on the same CDN, in the same per-slot map, as the art it generates itself. So there is
// nothing to swap in here — the resolved set already carries the right images. The only
// thing the page can't see from the artwork alone is who made it, and the backend answers
// that separately, per slot, in the family's `provenance` map.

/**
 * The family's own per-slot logo map — the authoritative one.
 *
 * Preferred over {@link brandLogoSource}, which reads the brand record. That record carries
 * the map in two places that can disagree, and it lags a replacement: it is refreshed by
 * the deployment callback, while this is read straight from casino-creation's source of
 * truth. {@link logoSetSource} falls back to the brand record while the family is still loading.
 *
 * Held to the SAME renderable-slot bar as {@link brandLogoSource}: an empty map, or one
 * carrying only the chrome placements this page hides, is not an answer — it is a map with
 * nothing in it. Returning it anyway made it win against the brand record, whose
 * complete set was then never consulted, and the page told the owner it had "No logo set
 * available yet" while their real logos were sitting one fallback away.
 */
const familyLogoSource = (family: LogoFamily | undefined): BrandLogoSource => {
  const logos = family?.logo_assets?.logos;
  return hasRenderableSlot(logos) ? logos : undefined;
};

/**
 * Add the favicon to a per-slot map as one more slot ({@link FAVICON_SLOT}), so it is resolved, downloaded and
 * replaced exactly like every other logo. Only a map that already fills the page gets one: a favicon on its own is
 * not a logo set, and letting it count would show a lone favicon where the page should fall back.
 */
const withFavicon = (logo: BrandLogoSource, favicon: LogoAssetLike | null | undefined): BrandLogoSource =>
  hasRenderableSlot(logo) && favicon?.src ? { ...logo, [FAVICON_SLOT]: { src: favicon.src, alt: favicon.alt } } : logo;

/**
 * The logo map the Logos page and the Website hub's Logos card resolve: the family's own (see
 * {@link familyLogoSource}), else the brand record's (see {@link brandLogoSource}), each with the favicon that SAME
 * record carries. The family keeps it at `logo_assets.favicon`; the brand record only on its deployment record
 * (`status_details.logoAssets.favicon`), never in `casino_data.details.logo`.
 *
 * Kept out of {@link brandLogoSource} itself on purpose: creatives reads that map for its ad and game logo sets,
 * where a favicon has no place.
 */
export const logoSetSource = (family: LogoFamily | undefined, brand: Brand | undefined): BrandLogoSource => {
  const fromFamily = familyLogoSource(family);
  if (fromFamily) return withFavicon(fromFamily, family?.logo_assets?.favicon);
  return withFavicon(brandLogoSource(brand), brand?.status_details?.logoAssets?.favicon);
};

/**
 * Label each logo with who made it, from the family's provenance map.
 *
 * `custom` means the owner supplied that artwork — whether or not Lumio then cleaned it up.
 * The graph names which provenance values count as owner-supplied, so this never hardcodes
 * them; without the graph every logo stays `generated`, which is the safer default (it
 * claims nothing about artwork we can't classify).
 */
export const applyProvenance = (
  resolved: ResolvedLogos,
  family: LogoFamily | undefined,
  graph: LogoGraph | undefined,
): ResolvedLogos => {
  const provenance = family?.provenance;
  if (!provenance || !graph) return resolved;
  const label = (logo: DisplayLogo): DisplayLogo => {
    const origin: LogoOrigin = isOwnerArtwork(graph, provenance[logo.id]) ? 'custom' : 'generated';
    return origin === logo.origin ? logo : { ...logo, origin };
  };
  return {
    ...resolved,
    primary: label(resolved.primary),
    variants: resolved.variants.map(label),
  };
};

// --- Logo deploy-step readiness ----------------------------------------------
// Raw content-pipeline step id that generates the casino's logo assets (the same
// step the "Technical details" deployment list humanises to "Logos").
const LOGO_DEPLOY_STEP = 'prepare_logo_assets';

/** State of the casino's logo-generation deploy step. */
export type LogoStepState = 'done' | 'generating' | 'failed' | 'none';

/**
 * Read the state of the brand's logo-generation deploy step (`prepare_logo_assets`)
 * from its real content-deployment pipeline. The brand's downloadable logo map is
 * only populated once this step completes; while it's queued/running the page must
 * show placeholders rather than the misleading schematic fallback art.
 *
 *  - `generating` — the step is queued or running (logos are on the way)
 *  - `failed`     — the step errored
 *  - `done`       — the step completed or was skipped
 *  - `none`       — no deployment pipeline is attached (a settled brand)
 */
export const logoStepState = (brand: Brand | undefined): LogoStepState => {
  const steps = brand?.status_details?.steps;
  if (!steps) return 'none';
  if (steps.pending.includes(LOGO_DEPLOY_STEP) || steps.active.includes(LOGO_DEPLOY_STEP)) return 'generating';
  if (LOGO_DEPLOY_STEP in steps.failed) return 'failed';
  return 'done';
};

// --- Logo-edit window (the 50-player replace gate) ---------------------------
// The owner may replace their logos until the casino has passed a registered-player cap.
// That count is the ONE bound: there is deliberately no grace period, so a casino that
// hasn't taken on players yet stays editable however long ago it launched. The cap is a
// brand-and-player protection (a site people have already signed up to can't be re-skinned
// under them, and burner brands can't be churned), not a deadline for settling on a look.
// Registered players ← the brand's player-roster size (Stats `select/players`, the
// Players page's source).
/** Replace stays open until this many players have registered. */
export const LOGO_EDIT_MAX_USERS = 50;

/**
 * The real logo-edit lock for the active brand: replacing a logo is open until more than
 * {@link LOGO_EDIT_MAX_USERS} players have registered. The threshold is returned
 * alongside the verdict so the banner copy stays data-driven.
 */
export const computeLogoEditWindow = (registeredUsers: number): LogoEditWindow => ({
  editable: registeredUsers <= LOGO_EDIT_MAX_USERS,
  registeredUsers,
  maxUsers: LOGO_EDIT_MAX_USERS,
  countKnown: true,
});

/**
 * The verdict when the player count could not be read at all.
 *
 * Locked, because this rule UNLOCKS below the cap and a roster whose registration half
 * failed reports only the players with activity — an under-count, which would hand out an
 * edit the brand's real size forbids. `countKnown: false` is what stops the section stating
 * a number it never measured.
 *
 * The alternative the page used to take was to hold the whole section on its loading
 * skeleton, which never resolved (the roster does not retry within a visit), so an unrelated
 * Stats outage blanked every logo, download and label with no explanation.
 */
export const unknownLogoEditWindow = (): LogoEditWindow => ({
  editable: false,
  registeredUsers: 0,
  maxUsers: LOGO_EDIT_MAX_USERS,
  countKnown: false,
});

/**
 * Whether a logo value is a per-slot map that would actually FILL this page: an object
 * holding at least one non-hidden slot with a usable `src`, exactly the set
 * {@link fromBrand} surfaces. A bare URL string, an empty map, and a map of nothing but
 * chrome placements (topNav / onboarding / footer) all fail, since each would leave the
 * variants grid empty and drop the page to the schematic fallback.
 */
const hasRenderableSlot = (logo: BrandLogoSource): logo is Record<string, LogoAssetLike | undefined> => {
  if (!logo || typeof logo !== 'object') return false;
  return Object.keys(logo).some((slot) => !HIDDEN_SLOTS.has(slot) && Boolean(logo[slot]?.src));
};

/**
 * The active brand's logo source, hardened against the two shapes the casino-instance
 * API ships: the brand record carries the per-slot variant map inline at
 * `casino_data.details.logo`, while some records reduce that to the single `original`
 * URL string and keep the map on the deployment record at
 * `status_details.logoAssets.logos`.
 *
 * Prefer the brand's OWN `casino_data.details.logo` map whenever it holds a renderable
 * slot. `status_details` describes a content-deployment pipeline, and a launched brand
 * can keep a settled record whose `logoAssets.logos` is stale or partial next to the
 * authoritative set on the brand itself, so preferring it surfaced the wrong (and
 * incomplete) variants on Website Logos. Fall back to `logoAssets.logos` for the
 * reduced-string era, then to the raw `details.logo` value so `resolveLogos` can apply
 * its bare-URL / schematic-fallback handling.
 *
 * Deliberately NOT mirrored in the top-bar BrandSelector / wallet resolvers: those pick
 * one compact chrome slot rather than the downloadable set, so the ordering that hurts
 * here does not apply to them.
 */
export const brandLogoSource = (brand: Brand | undefined): BrandDetails['logo'] | undefined => {
  const inlineMap = brand?.casino_data?.details?.logo;
  if (hasRenderableSlot(inlineMap)) {
    return inlineMap;
  }
  const variantMap = brand?.status_details?.logoAssets?.logos;
  if (hasRenderableSlot(variantMap)) {
    return variantMap;
  }
  return inlineMap;
};

export const resolveLogos = (logo: BrandLogoSource, mock: CasinoLogos, name: string): ResolvedLogos => {
  if (logo && typeof logo === 'object') {
    const real = fromBrand(logo, name);
    if (real.length > 0) {
      const [primary, ...variants] = real;
      return { primary, variants, source: 'brand' };
    }
  }
  // Fallback: schematic lockups from the mock variant template.
  const mocks = mock.variants.map((variant) => mockLogo(variant, name));
  const primary = mocks.find((_, index) => mock.variants[index].primary) ?? mocks[0];
  const variants = mocks.filter((entry) => entry !== primary);
  return { primary, variants, source: 'mock' };
};
